/**
 * ORO MVP API Client
 */

const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  import.meta.env.VITE_API_BASE_URL ||
  // Safe host-based defaults for subdomain architecture
  (hostname === 'oro.fit'
    ? 'https://api.oro.fit'
    : hostname === 'dev.oro.fit'
    ? 'https://api-dev.oro.fit'
    : hostname === 'qa.oro.fit'
    ? 'https://api-qa.oro.fit'
    : '/api'); // last resort for local dev proxies

if (import.meta.env.DEV) {
  console.log('[ORO] API_BASE_URL —', API_BASE_URL);
}

/**
 * Standard API Error class matching ARCHITECTURE_RULES.md
 * Backend returns: { error: { code, message, details? } }
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: Record<string, any>;

  constructor(message: string, code: string, status: number, details?: Record<string, any>) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

/**
 * Generic API request wrapper
 */
async function apiRequest<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${path}`;
  
  const response = await fetch(url, {
    ...options,
    credentials: 'include', // Include cookies for session
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  const contentType = response.headers.get('content-type') || '';

  if (!response.ok) {
    // Parse backend error response: { error: { code, message, details? } }
    if (contentType.includes('application/json')) {
      try {
        const body = await response.json();
        if (body.error) {
          // Backend standard error shape
          throw new ApiError(
            body.error.message || 'An error occurred',
            body.error.code || 'UNKNOWN_ERROR',
            response.status,
            body.error.details
          );
        }
      } catch (err) {
        if (err instanceof ApiError) throw err;
        // JSON parse failed or unexpected format
      }
    }
    // Fallback for non-JSON or malformed responses
    throw new ApiError(
      `HTTP ${response.status}: ${response.statusText}`,
      'HTTP_ERROR',
      response.status
    );
  }

  // 204 No Content or explicit empty response
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  // Non-JSON successful response (CF error pages, etc.)
  if (!contentType.includes('application/json')) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

/**
 * GET request
 */
export async function apiGet<T>(path: string): Promise<T> {
  return apiRequest<T>(path, { method: 'GET' });
}

/**
 * POST request
 */
export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return apiRequest<T>(path, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * PATCH request
 */
export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  return apiRequest<T>(path, {
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * PUT request
 */
export async function apiPut<T>(path: string, body?: unknown): Promise<T> {
  return apiRequest<T>(path, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * DELETE request
 */
export async function apiDelete<T>(path: string): Promise<T> {
  return apiRequest<T>(path, { method: 'DELETE' });
}

// Identity System Types (v0.8.0)
export const UserLevel = {
  FREE: 'free',
  RUNNER: 'runner',
  CLUB_LEADER: 'club_leader',
  FOUNDER: 'founder'
} as const;

export const SubscriptionLevel = {
  FREE: 'free',
  CLUB: 'club'
  // Future: PREMIUM, ENTERPRISE, etc.
} as const;

export const ClubRole = {
  OWNER: 'owner',
  MEMBER: 'member'
  // Future: ADMIN, MODERATOR, etc.
} as const;

export type UserLevel = typeof UserLevel[keyof typeof UserLevel];
export type SubscriptionLevel = typeof SubscriptionLevel[keyof typeof SubscriptionLevel];
export type ClubRole = typeof ClubRole[keyof typeof ClubRole];

// Type definitions for API responses
export interface User {
  id: string;
  email: string;
  // Identity & Naming (v0.8.0)
  public_display_name: string;
  first_name: string;
  last_name: string;
  public_name_mode: 'first_plus_initial' | 'full_name' | 'first_name_only';
  // Legacy field - maintained for compatibility
  name: string | null;
  avatar_url: string | null;
  // Profile fields (v0.7.0)
  tagline: string | null;
  birthday_day: number | null;
  birthday_month: number | null;
  location: string | null;
  primary_sport: string | null;
  secondary_sports: string[] | null;
  profile_image_url: string | null;
  // Identity system (v0.8.0)
  user_level: UserLevel;
  subscription_level: SubscriptionLevel | null;
  created_at: number;
  updated_at: number;
}

// Public profile response for public user endpoints (v0.8.0)
export interface PublicUserProfile {
  id: string;
  public_display_name: string;
  profile_image_url: string | null;
  user_level: UserLevel;
  tagline: string | null;
  location: string | null;
  primary_sport: string | null;
  created_at: number;
}

export interface MagicLinkRequestResponse {
  ok: boolean;
}

export interface MagicLinkConsumeResponse {
  user: User;
}

export interface RegistrationRequest {
  first_name: string;
  last_name: string;
  public_name_mode?: 'first_plus_initial' | 'full_name' | 'first_name_only';
}

export interface RegistrationResponse {
  user: User;
}

export interface CurrentUserResponse {
  user: User;
}

export interface PublicUserProfileResponse {
  profile: PublicUserProfile;
}

export interface Club {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  hero_image_url: string | null;
  avatar_image_url: string | null; // Issue #562
  subscription_level: string | null;
  role: 'owner' | 'leader' | 'moderator' | 'member';
  member_count: number;
  upcoming_event_count: number;
  created_at: number;
}

// GET /clubs returns array directly per API contract
export type MyClubsResponse = Club[];

export interface ClubDetail {
  club: {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    hero_image_url: string | null;
    avatar_image_url: string | null; // Issue #562
    subscription_level: string | null;
    owner_id: string;
    created_at: number;
    updated_at: number;
  };
  stats: {
    member_count: number;
    upcoming_event_count: number;
  };
  membership: {
    role: 'owner' | 'leader' | 'moderator' | 'member' | 'none';
    is_owner?: boolean;
  };
}

export interface JoinClubResponse {
  ok: boolean;
}

export interface LeaveClubResponse {
  ok: boolean;
}

export interface CreateClubRequest {
  name: string;
  description?: string;
  hero_image_url?: string;
}

export interface CreateClubResponse {
  club: {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    hero_image_url: string | null;
    owner_id: string;
    created_at: number;
    updated_at: number;
  };
}

export interface ClubMember {
  user_id: string;
  name: string;
  public_display_name: string;
  email: string;
  role: 'owner' | 'leader' | 'moderator' | 'member';
  user_level: UserLevel;
  subscription_level: SubscriptionLevel | null;
  profile_image_url: string | null;
  is_owner: boolean;
}

export interface ClubMembersResponse {
  members: ClubMember[];
}

export interface UpdateClubRequest {
  name?: string;
  description?: string;
  hero_image_url?: string;
}

export interface UpdateClubResponse {
  club: {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    hero_image_url: string | null;
    owner_id: string;
    created_at: number;
    updated_at: number;
    subscription_level: string | null;
  };
}

export interface TransferOwnershipRequest {
  new_owner_id: string;
}

export interface TransferOwnershipResponse {
  ok: boolean;
}

export interface DeleteClubResponse {
  ok: boolean;
}

export interface RemoveMemberResponse {
  ok: boolean;
}

export interface UpdateMemberRoleRequest {
  role: 'owner' | 'leader' | 'moderator' | 'member';
}

export interface UpdateMemberRoleResponse {
  ok: boolean;
  member: {
    user_id: string;
    role: 'owner' | 'leader' | 'moderator' | 'member';
  };
}

export interface Event {
  id: string;
  club_id: string;
  club_slug?: string;
  title: string;
  description: string | null;
  starts_at: number;
  ends_at: number | null;
  location_name: string | null;
  location_address: string | null;
  location_lat: number | null;
  location_lng: number | null;
  checkin_radius_m: number | null;
  checkin_window_before_min: number | null;
  checkin_window_after_min: number | null;
  distance_km: number | null;
  is_recurring: number;
  created_by: string;
  created_at: number;
  updated_at: number;
}

export interface EventDetailResponse {
  event: Event;
}

export interface CreateEventRequest {
  title: string;
  description?: string;
  starts_at: number;
  ends_at?: number;
  location_name?: string;
  location_address?: string;
  location_lat?: number;
  location_lng?: number;
  checkin_radius_m?: number;
  checkin_window_before_min?: number;
  checkin_window_after_min?: number;
  distance_km?: number;
}

export interface CreateEventResponse {
  event: Event;
}

export interface UpdateEventRequest {
  title?: string;
  description?: string;
  starts_at?: number;
  ends_at?: number;
  location_name?: string;
  location_address?: string;
  location_lat?: number;
  location_lng?: number;
  checkin_radius_m?: number;
  checkin_window_before_min?: number;
  checkin_window_after_min?: number;
  distance_km?: number;
}

export interface UpdateEventResponse {
  event: Event;
}

export interface ClubForEventCreation {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  my_role: 'owner' | 'leader';
  member_count: number;
}

export interface ClubsForEventCreationResponse {
  clubs: ClubForEventCreation[];
}

// Auth API calls
export const authApi = {
  requestMagicLink: (email: string, turnstileToken?: string, inviteCode?: string) =>
    apiPost<MagicLinkRequestResponse>('/auth/magic-link/request', { 
      email,
      ...(turnstileToken && { turnstile_token: turnstileToken }),
      ...(inviteCode && { invite_code: inviteCode })
    }),

  consumeMagicLink: (token: string) =>
    apiPost<MagicLinkConsumeResponse>('/auth/magic-link/consume', { token }),

  register: (data: RegistrationRequest) =>
    apiPost<RegistrationResponse>('/auth/register', data),

  getCurrentUser: () =>
    apiGet<CurrentUserResponse>('/auth/me'),

  logout: () =>
    apiPost<MagicLinkRequestResponse>('/auth/logout'),
};

// Clubs API calls
export const clubsApi = {
  getMyClubs: () =>
    apiGet<MyClubsResponse>('/clubs'),

  getClubById: (clubId: string) =>
    apiGet<ClubDetail>(`/clubs/id/${clubId}`),

  getClubBySlug: (slug: string) =>
    apiGet<ClubDetail>(`/clubs/${slug}`),

  createClub: (data: CreateClubRequest) =>
    apiPost<CreateClubResponse>('/clubs', data),

  joinClub: (clubId: string) =>
    apiPost<JoinClubResponse>(`/clubs/${clubId}/join`),

  leaveClub: (clubId: string) =>
    apiPost<LeaveClubResponse>(`/clubs/${clubId}/leave`),

  getClubMembers: (clubId: string) =>
    apiGet<ClubMembersResponse>(`/clubs/${clubId}/members`),

  updateClub: (clubId: string, data: UpdateClubRequest) =>
    apiPut<UpdateClubResponse>(`/clubs/${clubId}`, data),

  deleteClub: (clubId: string) =>
    apiDelete<DeleteClubResponse>(`/clubs/${clubId}`),

  transferOwnership: (clubId: string, data: TransferOwnershipRequest) =>
    apiPost<TransferOwnershipResponse>(`/clubs/${clubId}/transfer`, data),

  removeMember: (clubId: string, memberId: string) =>
    apiDelete<RemoveMemberResponse>(`/clubs/${clubId}/members/${memberId}`),
    
  updateMemberRole: (clubId: string, memberId: string, data: UpdateMemberRoleRequest) =>
    apiPut<UpdateMemberRoleResponse>(`/clubs/${clubId}/members/${memberId}/role`, data),
};

// Events API calls
export const eventsApi = {
  getClubsForEventCreation: () =>
    apiGet<ClubsForEventCreationResponse>('/events/clubs-for-creation'),

  getClubEvents: (clubId: string, params?: { from?: number; to?: number }) => {
    const query = new URLSearchParams();
    if (params?.from) query.append('from', params.from.toString());
    if (params?.to) query.append('to', params.to.toString());
    const queryString = query.toString();
    return apiGet<Event[]>(`/clubs/${clubId}/events${queryString ? `?${queryString}` : ''}`);
  },

  getEventById: (eventId: string) =>
    apiGet<EventDetailResponse>(`/events/${eventId}`),

  createEvent: (clubId: string, data: CreateEventRequest) =>
    apiPost<CreateEventResponse>(`/clubs/${clubId}/events`, data),

  updateEvent: (eventId: string, data: UpdateEventRequest) =>
    apiPatch<UpdateEventResponse>(`/events/${eventId}`, data),

  deleteEvent: (eventId: string) =>
    apiDelete<{ ok: boolean }>(`/events/${eventId}`),
};

// Check-in interfaces
export interface Checkin {
  id: string;
  event_id: string;
  user_id: string;
  checked_in_at: number;
  source: 'gps' | 'qr' | 'offline' | 'admin';
  lat: number | null;
  lng: number | null;
  created_at: number;
}

export interface CheckinRequest {
  lat?: number;
  lng?: number;
  source: 'gps' | 'qr' | 'offline' | 'secret_word';
  secret_word?: string;
}

export interface CheckinResponse {
  checkin: Checkin;
}

export interface CheckinWithUser extends Checkin {
  name: string;
}

// Profile API Types
export interface ProfileUpdateRequest {
  // Identity & Naming (v0.8.0)
  first_name?: string;
  last_name?: string;
  public_name_mode?: 'full' | 'first_last_initial' | 'first_only' | 'anonymous';
  // Legacy field - maintained for compatibility  
  name?: string;
  tagline?: string;
  birthday_day?: number | null;
  birthday_month?: number | null;
  location?: string;
  primary_sport?: string;
  secondary_sports?: string[];
}

export interface ProfileResponse {
  profile: User;
}

// Check-ins API calls
export const checkinsApi = {
  createCheckin: (eventId: string, data: CheckinRequest) =>
    apiPost<CheckinResponse>(`/events/${eventId}/checkins`, data),
  getCheckins: (eventId: string) =>
    apiGet<CheckinWithUser[]>(`/events/${eventId}/checkins`),
};

// Profile API calls
export const profileApi = {
  getProfile: () =>
    apiGet<ProfileResponse>('/profile'),
  updateProfile: (data: ProfileUpdateRequest) =>
    apiPut<ProfileResponse>('/profile', data),
  removeProfileImage: () =>
    apiDelete<{ ok: boolean }>('/profile/image'),
};

// Users API calls (public profiles)
export const usersApi = {
  getPublicProfile: (userId: string) =>
    apiGet<PublicUserProfileResponse>(`/users/${userId}`),
};

// Upload API calls
export const uploadApi = {
  uploadProfileImage: async (file: File): Promise<{ success: boolean; image_url: string; message: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    
    const url = `${API_BASE_URL}/upload/profile-image`;
    
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: formData, // Don't set Content-Type header, let browser set it for FormData
    });

    if (!response.ok) {
      try {
        const body = await response.json();
        if (body.error) {
          throw new ApiError(
            body.error.message || 'Upload failed',
            body.error.code || 'UPLOAD_ERROR',
            response.status
          );
        }
      } catch (err) {
        if (err instanceof ApiError) throw err;
      }
      throw new ApiError(
        `Upload failed: ${response.status} ${response.statusText}`,
        'UPLOAD_ERROR',
        response.status
      );
    }

    return response.json();
  },

  uploadClubChatImage: async (clubId: string, file: File): Promise<{ success: boolean; image_url: string; message: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    
    const url = `${API_BASE_URL}/upload/club-chat-image/${clubId}`;
    
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      try {
        const body = await response.json();
        if (body.error) {
          throw new ApiError(
            body.error.message || 'Chat image upload failed',
            body.error.code || 'UPLOAD_ERROR',
            response.status
          );
        }
      } catch (err) {
        if (err instanceof ApiError) throw err;
      }
      throw new ApiError(
        `Club chat image upload failed: ${response.status} ${response.statusText}`,
        'UPLOAD_ERROR',
        response.status
      );
    }

    return response.json();
  },
  
  removeProfileImage: () =>
    apiDelete<{ success: boolean; message: string }>('/upload/profile-image'),

  uploadClubHeroImage: async (clubId: string, file: File): Promise<{ success: boolean; image_url: string; message: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    
    const url = `${API_BASE_URL}/upload/club-hero-image/${clubId}`;
    
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      try {
        const body = await response.json();
        if (body.error) {
          throw new ApiError(
            body.error.message || 'Hero image upload failed',
            body.error.code || 'UPLOAD_ERROR',
            response.status
          );
        }
      } catch (err) {
        if (err instanceof ApiError) throw err;
      }
      throw new ApiError(
        `Hero image upload failed: ${response.status} ${response.statusText}`,
        'UPLOAD_ERROR',
        response.status
      );
    }

    return response.json();
  },

  removeClubHeroImage: async (clubId: string): Promise<{ success: boolean; message: string }> => {
    const url = `${API_BASE_URL}/upload/club-hero-image/${clubId}`;
    
    const response = await fetch(url, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok) {
      try {
        const body = await response.json();
        if (body.error) {
          throw new ApiError(
            body.error.message || 'Failed to remove hero image',
            body.error.code || 'DELETE_ERROR',
            response.status
          );
        }
      } catch (err) {
        if (err instanceof ApiError) throw err;
      }
      throw new ApiError(
        `Failed to remove hero image: ${response.status} ${response.statusText}`,
        'DELETE_ERROR',
        response.status
      );
    }

    return response.json();
  },
};
